// Horizontal category navigation strip — sits directly beneath the
// hero search pill on every screen size. Same 10 chips as the
// modals (FilterModal grid + WhereModal "Serviciu" panel); single
// source of truth in `./categories.js`.
//
// Self-contained on the URL (`?category=` and `?species=`).
//
// Arrow buttons at each end + edge fades only appear when the row
// actually overflows that direction (ResizeObserver + scroll
// listener). Native scrollbar hidden.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { CATEGORIES, getActiveCategoryId, commitCategoryToUrl } from './categories';

const SCROLL_STEP = 240;

export default function CategoryStrip() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = getActiveCategoryId(searchParams);

  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  const scrollBy = (delta) => {
    scrollerRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handleSelect = (entry) => {
    setSearchParams((prev) => commitCategoryToUrl(prev, entry.id === 'all' ? null : entry));
  };

  return (
    <div className="relative">
      {canScrollLeft && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-cream-100 to-transparent" aria-hidden="true" />
          <button
            type="button"
            onClick={() => scrollBy(-SCROLL_STEP)}
            aria-label="Derulează categoriile la stânga"
            className="absolute left-1 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-ink/15 bg-cream-50 text-ink-600 shadow-md transition-all hover:scale-105 hover:shadow-lg"
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}

      {canScrollRight && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-cream-100 to-transparent" aria-hidden="true" />
          <button
            type="button"
            onClick={() => scrollBy(SCROLL_STEP)}
            aria-label="Derulează categoriile la dreapta"
            className="absolute right-1 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-ink/15 bg-cream-50 text-ink-600 shadow-md transition-all hover:scale-105 hover:shadow-lg"
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      <div
        ref={scrollerRef}
        // Always justify-start. A previous revision applied
        // `lg:justify-center` because the original 10-chip set fit in
        // the max-w-7xl container at lg+, but the 13-chip set (one chip
        // per ServiceCategory enum) overflows even at 2xl. `justify-
        // center` with overflow pushes the leading chips behind the
        // scroller's left edge — "Toate" disappears off-screen and
        // "Toaletaj complet" renders half-clipped. justify-start keeps
        // the start of the row pinned to the visible edge; any spare
        // width at very wide viewports sits on the right, which is the
        // same pattern Airbnb's filter strip uses.
        //
        // px-4 md:px-6 reduces (does not eliminate) the overlap with
        // the scroll-arrow buttons. The buttons are absolute-positioned
        // at left-1 / right-1 and are w-9 wide — they cover the 4-40px
        // band from the container edge. With px-4 the first chip
        // starts at 16px, so the button still overlaps the chip's
        // leading edge by 24px on mobile and 16px on desktop (px-6).
        // Going wider (px-10+) would clear physically but push chips
        // far from the container edge and break alignment with the
        // page's px-4 sm:px-6 lg:px-8 outer padding. The chosen
        // values match Airbnb's filter strip, where the arrow
        // intentionally hovers over the leading chip as a "scroll
        // back" affordance.
        className="flex gap-8 overflow-x-auto px-4 pb-2 md:px-6 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
        role="tablist"
        aria-label="Categorii servicii"
      >
        {CATEGORIES.map((entry) => {
          const active = entry.id === activeId;
          const { Icon, label } = entry;
          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => handleSelect(entry)}
              className={`group flex flex-shrink-0 flex-col items-center gap-1.5 border-b-2 pb-2 transition-colors ${
                active
                  ? 'border-ink text-ink'
                  : 'border-transparent text-ink-500 hover:text-ink'
              }`}
            >
              <Icon className={`h-5 w-5 transition-opacity ${active ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} />
              <span className={`whitespace-nowrap text-xs font-medium transition-opacity ${active ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
